/**
 * order-history — the buyer's list of the orders this browser bought.
 *
 * This file owns the PAGE: the header, the four settled states (loading,
 * error, empty, list) and the cap note. The row is `<order-row-stub>` — a
 * paper stub with its counterfoil torn off along a real perforation.
 *
 * Three page-level judgements, settled:
 *
 * 1. A PAID ORDER WEARS NO STATUS CHROME. It is the default and the whole
 *    reason the list exists. Only the three states that change what the buyer
 *    can do are marked, and none of them is marked with a badge: the state is
 *    carried by the stub's own material (order-row-stub.view.ts).
 * 2. THE TREATMENT IS NEVER THE ONLY CARRIER. A refunded or cancelled row
 *    also says, in a plain sentence, that the tickets no longer work at the
 *    door, because any visual treatment is the first thing a skimming eye
 *    drops and "reads as live tickets" is the one failure this surface must
 *    not have.
 * 3. THE PER-BROWSER CEILING IS STATED IN THE HEADER, NOT ONLY IN THE EMPTY
 *    STATE — so the empty state confirms something already said rather than
 *    breaking bad news at the worst possible moment.
 *
 * `<brand-bar>` stays on this page too: a chrome link to the page you are
 * already on is what the brand mark itself does on every page it leads home
 * from, and giving brand-bar route awareness to hide one control costs a
 * subscription in a component that has none.
 */
import { area } from "@mhmo91/schmancy";
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { ORDER_HISTORY_LIMIT } from "@momo/types";
import { css, html } from "lit";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { organizationPathPrefix } from "../../workspace/organization.state.ts";
import "../brand-bar.view.ts";
import "./order-row-stub.view.ts";
import { orderHistory } from "./orders.state.ts";

@schmancyElement("order-history")
export class OrderHistory extends LocalizeMixin(SchmancyElement) {
	// The route owns the viewport: one page, one scrollport. Anchored to the
	// viewport (not to a parent percentage) so the chain is complete wherever
	// it mounts.
	static styles = [
		css`
			:host {
				display: block;
				height: 100dvh;
			}
		`,
	];

	override render() {
		const { status, orders } = orderHistory.value;
		return html`
			<div class="grid h-full grid-rows-[auto_minmax(0,1fr)]">
				<brand-bar class="mx-auto w-full max-w-2xl px-4 pt-6 sm:px-6"></brand-bar>

				<div
					${overflowWithin()}
					class="mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 pb-16 pt-9 sm:px-6"
				>
					<div class="flex flex-col gap-1">
						<schmancy-typography type="headline" token="sm">
							${this.t("storefront.orders.title")}
						</schmancy-typography>
						<schmancy-typography type="body" token="md" class="text-surface-onVariant">
							${this.t("storefront.orders.browserNote")}
						</schmancy-typography>
					</div>

					${choose(
						status,
						[
							[
								"loading",
								// Three rows at the settled row's own geometry, so nothing
								// jumps when the query answers. One live region for all of
								// them; the skeletons themselves announce nothing.
								() => html`
									<div
										role="status"
										aria-busy="true"
										aria-label=${this.t("storefront.orders.loadingAriaLabel")}
										class="flex flex-col gap-3"
									>
										${[0, 1, 2].map(
											() => html`
												<schmancy-skeleton
													class="h-28 w-full rounded-lg"
													aria-hidden="true"
												></schmancy-skeleton>
											`,
										)}
									</div>
								`,
							],
							[
								"error",
								() => html`
									<div class="my-auto flex flex-col items-center gap-3 text-center">
										<schmancy-icon class="text-4xl text-surface-onVariant"
											>cloud_off</schmancy-icon
										>
										<schmancy-typography type="headline" token="sm"
											>${this.t("storefront.orders.errorTitle")}</schmancy-typography
										>
										<schmancy-typography
											type="body"
											token="md"
											class="max-w-sm text-surface-onVariant"
											>${this.t("storefront.orders.errorBody")}</schmancy-typography
										>
									</div>
								`,
							],
						],
						() =>
							orders.length === 0
								? html`
										<div class="my-auto flex flex-col items-center gap-3 text-center">
											<schmancy-icon class="text-4xl text-surface-onVariant"
												>confirmation_number</schmancy-icon
											>
											<schmancy-typography type="headline" token="sm"
												>${this.t("storefront.orders.emptyTitle")}</schmancy-typography
											>
											<schmancy-typography
												type="body"
												token="md"
												class="max-w-md text-surface-onVariant"
												>${this.t("storefront.orders.emptyBody")}</schmancy-typography
											>
											<schmancy-button
												variant="filled tonal"
												@click=${() =>
													area.push({
														area: "root",
														component: "storefront",
														path: organizationPathPrefix.get() || "/",
														historyStrategy: "push",
													})}
												>${this.t("storefront.orders.emptyCta")}</schmancy-button
											>
										</div>
									`
								: html`
										<div class="flex flex-col gap-2">
											${repeat(
												orders,
												(order) => order.orderId,
												(order) => html`
													<order-row-stub
														.order=${order}
														@click=${() =>
															area.push({
																area: "root",
																component: "order",
																path: `${organizationPathPrefix.get()}/order`,
																params: { t: order.orderAccessToken },
																historyStrategy: "push",
															})}
													></order-row-stub>
												`,
											)}
										</div>
										${when(
											orders.length >= ORDER_HISTORY_LIMIT,
											// True whether the buyer has exactly this many or more —
											// nothing on this surface can tell those apart, so the
											// sentence deliberately claims only what is known.
											() => html`
												<schmancy-typography
													type="body"
													token="sm"
													class="text-center text-surface-onVariant"
													>${this.t("storefront.orders.cappedNote", {
														count: ORDER_HISTORY_LIMIT,
													})}</schmancy-typography
												>
											`,
										)}
									`,
					)}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"order-history": OrderHistory;
	}
}
